import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Heart, Brain, Scale, Briefcase } from 'lucide-react';
import CTA from '../../components/CTA';

const LisburnWeightManagement: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Weight Management Hypnotherapy Lisburn | Professional Lifestyle Change</title>
        <meta
          name="description"
          content="Weight management hypnotherapy serving Lisburn from Belfast clinic (8 miles). Address desk eating, business lunch habits, commuter stress eating. CNHC registered."
        />
        <meta name="keywords" content="weight loss hypnotherapy Lisburn, weight management Lisburn, professional lifestyle Lisburn, emotional eating Lisburn, mindful eating Lisburn" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/lisburn-weight-management/" />
        <meta property="og:title" content="Weight Management Hypnotherapy Lisburn | Professional Lifestyle Change" />
        <meta property="og:description" content="Weight management hypnotherapy serving Lisburn. Address desk eating, business lunch habits, sustainable lifestyle change for busy professionals." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Weight Management Lisburn",
            "description": "CNHC registered weight management hypnotherapy serving Lisburn from Belfast clinic. Specializing in professional lifestyle challenges, desk eating, commuter stress eating.",
            "url": "https://www.sharonclare-hypnotherapy.com/lisburn-weight-management/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Weight Management Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Lisburn",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Wellness Clinic (Ormeau Road)",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy Serving Lisburn
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Sustainable lifestyle change for busy professionals and commuters
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-4 mb-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">8 miles</span> from Lisburn via M1 • <span className="font-semibold">15-20 min drive</span>
                </p>
              </div>
              <div className="flex items-center justify-center space-x-4">
                <Train className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Train</span> to Botanic or City Hospital, the closest stations to the clinic
                </p>
              </div>
              <p className="text-gray-600 mt-3">Online sessions also available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy for Lisburn Professionals
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I serve the Lisburn community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, a short run from Lisburn up the M1 into Belfast and then south along the Ormeau Road. The drive is typically around 20 minutes off-peak, or you can take the frequent train service into Botanic or City Hospital, the closest stations to the clinic. For Lisburn professionals commuting to Belfast daily, my clinic is conveniently positioned to fit into your existing routine without adding significant travel time.
              </p>
              <p>
                As Northern Ireland's third-largest city with a strong professional class demographic, Lisburn residents face unique weight management challenges. Desk-based work, sedentary commuting, business lunches, after-work stress eating, and the time pressure of balancing career and family all contribute to weight gain that feels frustratingly difficult to control. If you're someone who knows exactly what they should be eating but finds yourself snacking at your desk, eating quickly between meetings, or stress eating in the evening after a long commute, you're experiencing a mindset problem, not a knowledge problem.
              </p>
              <p>
                Solution-focused hypnotherapy addresses the root causes of professional lifestyle weight gain. We work on stress management, emotional eating patterns, the habit of eating mindlessly at your desk, and the evening stress-eating cycle that undoes your good intentions. By teaching your nervous system to switch off the stress response and addressing the subconscious patterns driving your eating behaviors, sustainable weight loss becomes achievable without adding another stressful "diet" to your already busy life.
              </p>
              <p>
                For busy Lisburn professionals, I also offer online sessions via video call - ideal for fitting into a lunch break or attending from home in the evening. The flexibility of online sessions makes consistency easier, and consistency is what creates lasting change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Lifestyle Challenges */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Professional Lifestyle Weight Challenges
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <Briefcase className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Desk Eating and Mindless Snacking
                </h3>
                <p className="text-gray-700">
                  When you eat at your desk while working, your brain doesn't register that you've eaten. You're distracted, eating quickly, and not noticing fullness signals. This leads to consuming far more calories than you realize. Hypnotherapy helps you break the automatic habit of desk eating and reconnect with mindful eating - noticing when you're genuinely hungry and actually enjoying your food.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Car className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Commuter Stress Eating
                </h3>
                <p className="text-gray-700">
                  The drive between Lisburn and Belfast, especially during rush hour, creates chronic low-level stress. Many professionals find themselves eating in the car or immediately upon arriving home, using food to decompress from the stressful commute. Hypnotherapy teaches your nervous system healthier ways to transition from work mode to home mode without using food as a stress release valve.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Clock className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Evening Stress Eating
                </h3>
                <p className="text-gray-700">
                  After a long day at work, the commute, and dealing with family responsibilities, many professionals find themselves eating in the evening not from hunger but from exhaustion and stress. The biscuit tin, crisps, wine, and snacking while watching TV undo all the good choices made during the day. Addressing the underlying stress and exhaustion is key to breaking this pattern.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Heart className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Business Lunches and Work Events
                </h3>
                <p className="text-gray-700">
                  Professional life often involves business lunches, work events, and social eating situations where you feel pressured to eat and drink more than you want. Hypnotherapy builds your confidence to make choices aligned with your goals even in social situations, without feeling deprived or different. You learn to enjoy social eating without it derailing your progress.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Hypnotherapy Approach */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              How Hypnotherapy Creates Sustainable Change
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Hypnotherapy for weight management works at a subconscious level, addressing the automatic patterns and stress responses that drive eating behaviors. Rather than relying on willpower (which is a limited resource, especially when you're tired and stressed), we create changes that feel natural and effortless.
              </p>
              <p>
                When your body is stuck in stress mode - which it often is when you're juggling a demanding career, commuting, and family responsibilities - your hunger hormones change. Cortisol increases, ghrelin (the hunger hormone) increases, and your brain becomes hyper-focused on high-calorie comfort foods. This isn't a character flaw - it's biology. Your stressed nervous system is trying to keep you safe by encouraging you to seek quick energy.
              </p>
              <p>
                By teaching your nervous system to switch off the stress response, hypnotherapy naturally reduces cravings and emotional eating. You'll find yourself making better food choices without the internal battle, noticing when you're full and actually stopping, and breaking the automatic habits like desk snacking or evening stress eating. The changes happen gradually but feel sustainable because they're not based on restriction or deprivation.
              </p>
              <p>
                For busy Lisburn professionals, the time investment is minimal - typically 4-8 sessions over a few months, plus daily audio recordings (10 minutes). Compare this to the years spent struggling with yo-yo dieting, and it's a remarkably efficient use of your time with lasting results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Lisburn Clients Say About Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "As a Lisburn professional commuting to Belfast daily, desk eating and evening stress eating had become automatic. Sharon helped me understand why I was eating when I wasn't hungry. After 5 sessions, I've lost a stone and a half, and more importantly, I feel in control around food for the first time in years."
                </p>
                <p className="font-semibold text-gray-900">J.M., Lisburn - Professional Lifestyle Change</p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Online sessions were perfect for my schedule - I could fit them in during my lunch break. Sharon's approach addressed the stress and time pressure that was driving my eating habits. I've lost 2 stone over 6 months and it feels sustainable, not like another diet that will fail."
                </p>
                <p className="font-semibold text-gray-900">R.K., Hilden, Lisburn - Busy Professional</p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The evening stress eating after the commute was killing me. I knew I shouldn't be eating biscuits at 10pm, but I couldn't seem to stop. Hypnotherapy helped me manage the commuter stress in healthier ways. I've lost over 2 stone and my evenings are so much calmer now."
                </p>
                <p className="font-semibold text-gray-900">T.D., Moira - Commuter Stress Eating</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Lisburn Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How can hypnotherapy help with desk eating and work snacking?
                </h3>
                <p className="text-gray-700">
                  Desk eating and work snacking are automatic habits driven by stress, boredom, and the work environment. When you eat while distracted, your brain doesn't register satiety properly, leading to overeating without even realizing it. Hypnotherapy breaks these automatic patterns by working at a subconscious level. You'll find yourself naturally choosing to step away from your desk to eat mindfully, noticing when you're genuinely hungry versus eating out of habit, and feeling satisfied with appropriate portions. The changes feel natural rather than forced because we're reprogramming the subconscious habits driving the behavior.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  I'm too busy for regular appointments - can online sessions work?
                </h3>
                <p className="text-gray-700">
                  Online sessions are ideal for busy Lisburn professionals. You can attend from your office during a lunch break, from home in the evening, or even during a work-from-home day. There's no travel time at all, and the flexibility makes consistency much easier. Hypnotherapy works just as effectively online as in-person - the therapeutic relationship and hypnotic process aren't diminished by the screen. Many busy professionals actually prefer online sessions precisely because they can maintain consistency without adding more time pressure to their already packed schedules.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can you help with evening stress eating after work?
                </h3>
                <p className="text-gray-700">
                  Evening stress eating is extremely common among professionals and commuters. After a demanding day and the stressful drive home, your nervous system is in overdrive and seeks quick relief - often through food. Hypnotherapy addresses this at the root by teaching your nervous system healthier ways to decompress. We work on reducing your overall stress response, building alternative coping mechanisms, and breaking the automatic pattern of reaching for food when you're not actually hungry. Most clients notice within a few sessions that the evening eating urges naturally diminish as their stress levels reduce.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How do I manage business lunches and work events?
                </h3>
                <p className="text-gray-700">
                  Business lunches and work events can feel like social minefields when you're trying to manage your weight. Hypnotherapy builds your confidence to make choices aligned with your goals without feeling deprived, awkward, or drawing attention. You'll learn to enjoy social eating situations, have one glass of wine rather than three, stop when you're satisfied rather than uncomfortably full, and handle peer pressure around food without feeling like you're missing out. The key is changing your mindset from deprivation ("I can't have that") to empowerment ("I choose not to have that because it doesn't serve my goals").
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How long will it take to see results?
                </h3>
                <p className="text-gray-700">
                  Most clients notice changes within the first 2-3 sessions - reduced cravings, better awareness of hunger and fullness, improved sleep, feeling more in control around food. Weight loss typically follows over the next few weeks and months as your eating patterns naturally improve. The timeline varies depending on how much you want to lose and how deeply entrenched your emotional eating patterns are, but 4-8 sessions over 2-4 months is typical. This might seem longer than a crash diet, but the results are sustainable. You're not just losing weight - you're changing your relationship with food permanently.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is hypnotherapy suitable for someone with a demanding corporate job?
                </h3>
                <p className="text-gray-700">
                  Absolutely. In fact, busy professionals often benefit most from hypnotherapy because it addresses the specific stressors driving weight gain in professional lifestyles - work pressure, time stress, perfectionism, high standards, and chronic low-level anxiety. The approach is evidence-based, efficient, and focused on practical solutions that fit into a busy life. You don't need to overhaul your entire lifestyle - small, sustainable changes at a subconscious level create significant results. Many Lisburn professionals appreciate the professional, no-nonsense approach backed by neuroscience and 15 years of nursing experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default LisburnWeightManagement;
